import { useMemo } from 'react';
import { format } from 'date-fns';
import ReactApexChart from 'react-apexcharts';
import { CoinDistributionPoint } from '@/services/wallet.service';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface CoinDistributionChartProps {
  data: CoinDistributionPoint[];
  title?: string;
  height?: number;
}

/**
 * Line chart showing coin distribution over time (earned, spent, net)
 */
export function CoinDistributionChart({
  data,
  title = 'Coin Distribution',
  height = 350,
}: CoinDistributionChartProps) {
  const chartOptions = useMemo(() => {
    const dates = data.map((point) => format(new Date(point.date), 'MMM dd'));
    const earned = data.map((point) => point.earned);
    const spent = data.map((point) => point.spent);
    const net = data.map((point) => point.net);

    return {
      series: [
        {
          name: 'Earned',
          data: earned,
          color: '#10b981', // green-500
        },
        {
          name: 'Spent',
          data: spent,
          color: '#ef4444', // red-500
        },
        {
          name: 'Net',
          data: net,
          color: '#3b82f6', // blue-500
        },
      ],
      options: {
        chart: {
          type: 'line' as const,
          height,
          toolbar: {
            show: true,
            tools: {
              download: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: false,
              reset: true,
            },
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 800,
          },
        },
        stroke: {
          width: 3,
          curve: 'smooth' as const,
        },
        xaxis: {
          categories: dates,
          title: {
            text: 'Date',
          },
          labels: {
            rotate: -45,
            rotateAlways: false,
          },
        },
        yaxis: {
          title: {
            text: 'Coins',
          },
          labels: {
            formatter: (value: number) => value.toLocaleString(),
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (value: number) => value.toLocaleString() + ' coins',
          },
        },
        legend: {
          position: 'top' as const,
          horizontalAlign: 'right' as const,
        },
        grid: {
          borderColor: '#e5e7eb',
          strokeDashArray: 4,
        },
        markers: {
          size: 4,
          hover: {
            size: 6,
          },
        },
      },
    };
  }, [data, height]);

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <ReactApexChart
          options={chartOptions.options}
          series={chartOptions.series}
          type="line"
          height={height}
        />
      </CardContent>
    </Card>
  );
}
